<template>
  <div>
    <v-form ref="form">
      <v-row align="center">
        <!-- shopper coupons switch -->
        <v-col cols="12">
          <editor-switch-row label="Shopper Coupons">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
              <v-switch
                v-bind="props"
                v-model="couponsEnabled"
                data-testid="ct-experience-coupons"
                density="compact"
                hide-details
                :disabled="
                  !storefrontPricingEnabled ||
                    usedCouponList.length > 0 ||
                    $store.getters.getIsNewOrderAdded ||
                    campaign.has_orders ||
                    campaign.complete
                "
                :key="couponsEnabledKey"
              >
              </v-switch>
              </template>

              <span v-if="disableFields || $store.getters.getIsNewOrderAdded">
                Coupons disabled after orders have been placed.
              </span>
              <span v-else-if="!storefrontPricingEnabled"
                >Storefront pricing must be enabled to add coupons
              </span>
              <span v-else
                >Allow shoppers to enter coupon codes on the storefront to get a
                certain percent off their order</span
              >
            </v-tooltip>
          </editor-switch-row>
        </v-col>
        <!-- divider -->
        <v-col class="feature-divider-flex" cols="12" v-if="couponsEnabled">
          <v-divider />
        </v-col>
        <!-- shopper coupons - coupon list -->
        <v-col
          cols="12"
          v-if="couponsEnabled && storefrontPricingEnabled"
          class="feature-drawer coupon-scope pa-3"
        >
          <v-row>
            <v-col cols="12" data-testid="ct-experience-coupon-list">
              <!-- ZONE 1: header -- title (+ active count) + filter + actions -->
              <div
                class="d-flex align-center flex-wrap ga-2 mb-2"
                data-testid="ct-experience-coupon-header"
              >
                <div class="coupon-list-heading">
                  Coupon list
                  <span v-if="activeCouponCount > 0" class="coupon-active-count">
                    ({{ activeCouponCount }} active)
                  </span>
                </div>
                <v-text-field
                  v-model="couponCodeFilter"
                  label="Filter by code"
                  data-testid="ct-experience-coupon-filter"
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  prepend-inner-icon="search"
                  class="coupon-filter-field"
                />
                <v-spacer />
                <v-btn
                  @click="downloadCsv"
                  data-testid="ct-experience-download-csv"
                  variant="outlined"
                  class="extended-fab"
                  rounded="pill"
                  size="small"
                  color="black"
                  :disabled="visibleCoupons.length === 0"
                >
                  <v-icon class="mr-2 extended-fab-icon" icon="download" />
                  <v-spacer />
                  <div>Download CSV</div>
                </v-btn>
                <v-btn
                  @click="openDeleteVisibleDialog"
                  data-testid="ct-experience-delete-visible"
                  variant="outlined"
                  class="extended-fab"
                  rounded="pill"
                  size="small"
                  color="black"
                  :disabled="campaign.complete || deletableVisibleCount === 0"
                >
                  <v-icon class="mr-2 extended-fab-icon" icon="delete_sweep" />
                  <v-spacer />
                  <div>Delete visible</div>
                </v-btn>
                <v-btn
                  @click="openGenerateCouponsDialog"
                  data-testid="ct-experience-generate-coupons"
                  variant="outlined"
                  class="extended-fab"
                  rounded="pill"
                  size="small"
                  color="black"
                  :disabled="campaign.complete"
                >
                  <v-icon class="mr-2 extended-fab-icon" icon="auto_awesome" />
                  <v-spacer />
                  <div>Generate Coupons</div>
                </v-btn>
              </div>

              <!-- Bulk-delete result summary (transient, after a batch) -->
              <div
                v-if="deleteVisibleResult"
                class="mb-3 coupon-bulk-result"
                data-testid="ct-experience-delete-visible-result"
              >
                {{ deleteVisibleResult }}
              </div>

              <!-- ZONE 2: add-row -- single compose zone, washed background -->
              <v-row
                class="coupon-add-row"
                align="center"
                dense
                data-testid="ct-experience-add-row"
              >
                <v-col cols="12" sm="3" md="3">
                  <v-text-field
                    ref="addRowNameField"
                    label="Coupon Code"
                    density="compact"
                    variant="outlined"
                    :rules="couponCodeRules"
                    v-model="addRowCoupon.name"
                    data-testid="ct-experience-add-row-name"
                    :disabled="campaign.complete"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="3" md="3">
                  <v-select
                    label="Coupon Type"
                    v-model="addRowCoupon.coupon_type"
                    :items="['Percentage', 'Flat Rate']"
                    density="compact"
                    variant="outlined"
                    hide-details
                    data-testid="ct-experience-add-row-type"
                    :disabled="campaign.complete"
                  />
                </v-col>
                <v-col cols="12" sm="3" md="3">
                  <v-text-field
                    :prefix="addRowCoupon.coupon_type === 'Percentage' ? '%' : '$'"
                    :label="addRowCoupon.coupon_type"
                    type="number"
                    density="compact"
                    variant="outlined"
                    hide-details
                    v-model="addRowAmount"
                    data-testid="ct-experience-add-row-amount"
                    :disabled="campaign.complete"
                  />
                </v-col>
                <!--
                  At the sm breakpoint this column used to be a single
                  grid column -- about 50px -- while the "Add Coupon"
                  label needs roughly 75px plus its icon. The button was
                  narrower than its own content, which is the other half
                  of Jake's overflow report (the first half being the
                  fixed-height fab chrome, handled in the style block).
                  The add row is now an even four-way split at both sm
                  and md, so the pill has a column it can hold its label
                  in. Both breakpoints still sum to 12; a test checks
                  that arithmetic, because an over-budget row silently
                  wraps the pill onto its own line.
                -->
                <v-col cols="12" sm="3" md="3" class="d-flex align-center">
                  <v-btn
                    @click="addCouponButtonPress"
                    data-testid="ct-experience-add-coupon"
                    variant="flat"
                    class="add-order-btn extended-fab"
                    rounded="pill"
                    size="small"
                    color="black"
                    :disabled="campaign.complete"
                  >
                    <v-icon color="white" class="mr-2 extended-fab-icon" icon="add" />
                    <v-spacer />
                    <div style="color: white">Add Coupon</div>
                  </v-btn>
                </v-col>
              </v-row>

              <!-- ZONE 3: read-only list -->
              <div
                v-if="coupons.length > 0"
                class="coupon-column-headers d-flex"
                data-testid="ct-experience-coupon-column-headers"
              >
                <div class="coupon-col-code">Code</div>
                <div class="coupon-col-type">Type</div>
                <div class="coupon-col-amount">Amount</div>
                <div class="coupon-col-actions"></div>
              </div>
              <div
                v-for="item in visibleCoupons"
                :key="item.name"
                class="coupon-row d-flex align-center"
                :data-testid="`ct-experience-coupon-row-${item.name}`"
              >
                <div class="coupon-col-code coupon-row-code">{{ item.name }}</div>
                <div class="coupon-col-type">
                  <v-chip
                    size="small"
                    :data-testid="`ct-experience-coupon-type-pill-${item.name}`"
                  >
                    {{ item.coupon_type }}
                  </v-chip>
                </div>
                <div class="coupon-col-amount coupon-row-amount">
                  {{ formatCouponAmount(item) }}
                </div>
                <div class="coupon-col-actions d-flex align-center">
                  <span
                    v-if="isCouponActive(item)"
                    :title="isDeleteLockedByUnfrozenOrders(item) ? legacyLockReason : undefined"
                  >
                    <v-btn
                      height="20px"
                      width="20px"
                      icon variant="plain"
                      @click="deleteCouponByRef(item)"
                      :data-testid="`ct-experience-delete-coupon-${item.name}`"
                      :disabled="campaign.complete || isDeleteLockedByUnfrozenOrders(item)"
                    >
                      <v-icon icon="delete" />
                    </v-btn>
                  </span>
                  <v-btn
                    v-else
                    height="20px"
                    width="20px"
                    icon variant="plain"
                    @click="reactivateCouponByRef(item)"
                    data-testid="ct-experience-reactivate-coupon"
                    :disabled="campaign.complete"
                  >
                    <v-icon icon="restore" />
                  </v-btn>
                </div>
              </div>

              <div
                v-if="legacyLockedCouponNames.length > 0"
                class="mt-3 md-caption-no-spacing coupon-lock-note"
                data-testid="ct-experience-coupon-legacy-lock-note"
              >
                {{ legacyLockedCouponNames.join(", ") }}
                cannot
                be deleted. {{ legacyLockReason }}
              </div>

              <div v-if="hasDeletedCoupons" class="mt-3 coupon-footer">
                <v-switch
                  v-model="showDeleted"
                  label="Show deleted coupons"
                  density="compact"
                  hide-details
                  data-testid="ct-experience-show-deleted-coupons"
                />
                <div class="md-caption-no-spacing coupon-lock-note">
                  A deleted coupon cannot be used on new orders. Orders that
                  already used it keep its coupon rate.
                </div>
              </div>
            </v-col>
          </v-row>
        </v-col>
        <generate-coupons-dialog
          ref="generateCouponsDialog"
          :existing-codes="existingCouponCodes"
          @generated="onCouponsGenerated"
        />
        <!-- Bulk soft-delete confirmation -->
        <v-dialog
          v-model="showDeleteVisibleDialog"
          max-width="480"
          persistent
        >
          <v-card
            class="pa-2"
            data-testid="ct-experience-delete-visible-confirm"
          >
            <v-card-title>
              <div class="md-subheader-no-spacing">Delete visible coupons</div>
            </v-card-title>
            <v-card-text>
              Soft-delete {{ deletableVisibleCount }} visible coupons? You can
              restore them via Show deleted.
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                variant="text"
                @click="showDeleteVisibleDialog = false"
                data-testid="ct-experience-delete-visible-confirm-cancel"
              >
                Cancel
              </v-btn>
              <v-btn
                variant="flat"
                color="black"
                @click="confirmDeleteVisible"
                data-testid="ct-experience-delete-visible-confirm-ok"
              >
                Delete
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!-- Per-row delete of a coupon orders already used. The orders keep
             the coupon terms frozen on them at checkout, so the delete is
             allowed; this says so before it happens (Jake, 2026-10-09). -->
        <v-dialog
          :model-value="!!pendingUsedCouponDelete"
          max-width="480"
          persistent
        >
          <v-card
            class="pa-2"
            data-testid="ct-experience-delete-used-coupon-confirm"
          >
            <v-card-title>
              <div class="md-subheader-no-spacing">Delete a used coupon</div>
            </v-card-title>
            <v-card-text data-testid="ct-experience-delete-used-coupon-warning">
              {{ pendingUsedCouponOrderCount }}
              {{ pendingUsedCouponOrderCount === 1 ? "order" : "orders" }}
              already used {{ pendingUsedCouponName }}.
              {{ pendingUsedCouponOrderCount === 1 ? "That order" : "Those orders" }}
              will still be charged the coupon rate even though the coupon is
              deleted. Shoppers will not be able to use
              {{ pendingUsedCouponName }} on new orders.
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                variant="text"
                @click="cancelDeleteUsedCoupon"
                data-testid="ct-experience-delete-used-coupon-confirm-cancel"
              >
                Cancel
              </v-btn>
              <v-btn
                variant="flat"
                color="black"
                @click="confirmDeleteUsedCoupon"
                data-testid="ct-experience-delete-used-coupon-confirm-ok"
              >
                Delete
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!-- divider -->
        <v-col class="feature-divider-flex" cols="12" v-if="couponsEnabled">
          <v-divider />
        </v-col>
        <v-col cols="12" v-else>
          <v-divider />
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import rules from "@/mixins/rules";
import {
  buildCouponCsv,
  buildNewCoupon,
  formatCouponAmount,
  hasDeletedCoupons,
  isCouponActive,
  isCouponVisible,
  reactivateCoupon as reactivateCouponHelper,
  softDeleteCoupon,
} from "./couponHelpers.js";
import GenerateCouponsDialog from "./GenerateCouponsDialog.vue";
import EditorSwitchRow from "./components/EditorSwitchRow.vue";

export default {
  components: { GenerateCouponsDialog, EditorSwitchRow },

  props: {
    campaign: Object,
    editMode: Boolean,
    disableFields: Boolean,
    storefrontPricingEnabled: Boolean,
    consolidateTaxFeeEnabled: Boolean,
  },

  mixins: [rules],

  emits: ["campaign-changed", "update:coupons-enabled", "consolidate-conflict"],

  data() {
    return {
      coupons: [],
      couponsEnabled: false,
      couponsEnabledKey: 0,
      couponKeys: [],
      usedCouponSummary: [],
      showDeleted: false,
      // ZONE 1 (header) controls for the list-management features:
      // a case-insensitive code filter, the bulk soft-delete confirm
      // dialog, and the transient result summary shown after a batch.
      couponCodeFilter: "",
      showDeleteVisibleDialog: false,
      deleteVisibleResult: "",
      // The coupon a per-row delete is waiting to confirm because orders
      // already used it; null when no warning is open.
      pendingUsedCouponDelete: null,
      // ZONE 2 (add-row) state: a single in-progress coupon draft, kept
      // separate from `coupons` so the list rows can stay purely
      // read-only. Cleared after a successful Add.
      addRowCoupon: buildNewCoupon(),
    };
  },

  watch: {
    couponsEnabled(enabled) {
      if (enabled && this.consolidateTaxFeeEnabled) {
        this.couponsEnabled = false;
        this.$emit("consolidate-conflict");
      }

      this.$emit("campaign-changed");
      this.$emit("update:coupons-enabled", this.couponsEnabled);
      this.couponsEnabledKey++;
    },
    // Clear the "N deleted, M skipped" bulk-delete result banner when the
    // filter changes -- it describes a prior delete over a different visible
    // set and would otherwise linger and mislead.
    couponCodeFilter() {
      this.deleteVisibleResult = "";
    },
  },

  computed: {
    couponCodeRules() {
      const rules = [];
      rules.push((v) => this.isUniqueAddRowCode(v) || "Duplicate coupon code");
      rules.push((v) => !!v || "Required");
      return rules;
    },
    /**
     * Two-way bridge onto the add-row's amount field, keyed by the
     * currently-selected coupon_type ("percentage" | "flat rate").
     */
    addRowAmount: {
      get() {
        const key = this.addRowCoupon.coupon_type.toLowerCase();
        return this.addRowCoupon[key];
      },
      set(value) {
        const key = this.addRowCoupon.coupon_type.toLowerCase();
        this.addRowCoupon[key] = value;
      },
    },
    /**
     * True if any coupon in the list is soft-deleted.
     * Drives visibility of the "Show deleted" toggle.
     */
    hasDeletedCoupons() {
      return hasDeletedCoupons(this.coupons);
    },
    /**
     * Count of active (non-soft-deleted) coupons, shown next to the
     * "Coupon list" header title.
     */
    activeCouponCount() {
      return this.coupons.filter((c) => this.isCouponActive(c)).length;
    },
    /**
     * Names of all coupons currently in the list (active and
     * soft-deleted). Passed to the Generate Coupons dialog so it can
     * avoid colliding with them when generating a fresh batch.
     */
    existingCouponCodes() {
      return this.coupons
        .map((c) => c && c.name)
        .filter((name) => !!name);
    },
    /**
     * THE single source of truth for "which coupons are visible right
     * now." Narrowed by BOTH the Show-deleted toggle (via isCouponVisible)
     * AND the case-insensitive code-substring filter. The list render
     * (v-show membership), the bulk-delete target, and the CSV export all
     * read this one set -- do not recompute "visible" anywhere else
     * (design-philosophy.md: single-source-state).
     */
    visibleCoupons() {
      const needle = (this.couponCodeFilter || "").trim().toLowerCase();
      return this.coupons.filter((c) => {
        if (!isCouponVisible(c, this.showDeleted)) return false;
        if (!needle) return true;
        return (c.name || "").toLowerCase().includes(needle);
      });
    },
    /**
     * Count of coupons in the visible set that a bulk delete would
     * actually soft-delete: currently active AND not locked (redeemed on
     * a placed order). Drives the Delete-visible button's disabled state
     * and the confirm dialog's count.
     */
    /**
     * Active coupons whose delete stays locked: an order that used them
     * predates coupon rates being saved with the order, so its charge
     * still reads the live coupon list.
     */
    legacyLockedCouponNames() {
      return this.coupons
        .filter((c) => isCouponActive(c) && this.isDeleteLockedByUnfrozenOrders(c))
        .map((c) => c.name);
    },
    legacyLockReason() {
      return (
        "Some orders that used it were placed before coupon rates were " +
        "saved with each order, so deleting it would have those orders " +
        "charged full price."
      );
    },
    /** Codes any (non-deleted) order carries, canceled orders included. */
    usedCouponList() {
      return this.usedCouponSummary.map((row) => row.code);
    },
    pendingUsedCouponName() {
      return this.pendingUsedCouponDelete ? this.pendingUsedCouponDelete.name : "";
    },
    pendingUsedCouponOrderCount() {
      return this.usedOrderCount(this.pendingUsedCouponName);
    },
    deletableVisibleCount() {
      return this.visibleCoupons.filter((c) => this.isCouponDeletable(c))
        .length;
    },
  },

  methods: {
    formatCouponAmount,

    /**
     * Returns coupon data for campaign save.
     * Filters out incomplete coupons (active and soft-deleted) and trims
     * coupon names. Soft-deleted entries are preserved so historical
     * reports can still resolve them (Order Settings Snapshot Plan, Fix 2).
     */
    getData() {
      const coupons = this.coupons.filter(
        (coupon) =>
          coupon["name"] &&
          ((coupon["coupon_type"] === "Percentage" && coupon["percentage"]) ||
            (coupon["coupon_type"] === "Flat Rate" && coupon["flat rate"]))
      );

      coupons.forEach((coupon) => {
        coupon["name"] = coupon["name"].trim();
      });

      return {
        coupons_enabled: this.couponsEnabled,
        coupons: coupons,
      };
    },

    /**
     * Loads coupon state from a campaign object.
     * Called by the orchestrator when campaign data is available.
     * Default-true semantics: legacy entries with no `active` field
     * are treated as active (Order Settings Snapshot Plan, Fix 2).
     */
    loadFromCampaign(campaign) {
      this.couponsEnabled = campaign.coupons_enabled;
      if (this.couponsEnabled) {
        const tmpCoupons = campaign.coupons;
        tmpCoupons.forEach((coupon) => {
          if (!coupon["coupon_type"]) {
            coupon["coupon_type"] = "Percentage";
          }
          // Default-true: leave existing entries (incl. legacy with no
          // `active` field) as-is. We treat undefined as active.
        });
        this.coupons = tmpCoupons;
      }
    },

    /**
     * Receives ``GET /campaign/getUsedCouponSummary/<id>``: one row per code
     * a (non-deleted) order carries, with ``order_count`` and
     * ``has_unfrozen_orders``. A used coupon cannot be edited or
     * bulk-deleted; a per-row delete asks first (``deleteCouponByRef``), and
     * is locked while an order that used it has no saved coupon rate.
     */
    loadUsedCouponSummary(rows) {
      this.usedCouponSummary = Array.isArray(rows) ? rows : [];
    },

    /**
     * ZONE 2 (add-row) submit handler. Validates the draft coupon
     * (required code + uniqueness, same rules the old per-row editable
     * fields used), appends a correctly-shaped coupon to the list,
     * emits campaign-changed for autosave, and clears the add-row for
     * the next entry. Field-level errors surface via couponCodeRules on
     * the add-row's Coupon Code v-text-field.
     */
    addCouponButtonPress() {
      const name = (this.addRowCoupon.name || "").trim();
      if (!name || !this.isUniqueAddRowCode(name)) {
        return;
      }

      const newCoupon = { ...this.addRowCoupon, name };
      this.couponKeys.push(Math.floor(Math.random() * 100000));
      this.coupons.push(newCoupon);
      this.$emit("campaign-changed");

      this.resetAddRow();
    },

    /**
     * Clears the add-row draft back to a fresh, empty coupon so the
     * next Add starts blank.
     */
    resetAddRow() {
      this.addRowCoupon = buildNewCoupon();
      this.$nextTick(() => {
        if (
          this.$refs.addRowNameField &&
          typeof this.$refs.addRowNameField.resetValidation === "function"
        ) {
          this.$refs.addRowNameField.resetValidation();
        }
      });
    },

    /**
     * Opens the bulk "Generate Coupons" modal.
     */
    openGenerateCouponsDialog() {
      this.$refs.generateCouponsDialog.open();
    },

    /**
     * Appends a batch of generated coupons (from the Generate Coupons
     * modal) to the coupon list, using the same array + campaign-changed
     * emit path as manually-added coupons so they autosave identically.
     */
    onCouponsGenerated(generatedCoupons) {
      generatedCoupons.forEach((coupon) => {
        this.couponKeys.push(Math.floor(Math.random() * 100000));
        this.coupons.push(coupon);
      });
      this.$emit("campaign-changed");
    },

    /**
     * Soft-delete: mark the coupon inactive instead of removing it from
     * the array. Past orders that used this code can still resolve the
     * definition for reports (Order Settings Snapshot Plan, Fix 2).
     *
     * Unsaved entries (no name) are hard-removed because they pose no
     * historical-order risk.
     */
    deleteCoupon(index) {
      const existing = this.coupons[index];
      if (!existing) return;
      const updated = softDeleteCoupon(existing);
      if (updated === null) {
        // Hard-remove unsaved entry
        this.coupons.splice(index, 1);
        this.couponKeys.splice(index, 1);
        return;
      }
      this.coupons.splice(index, 1, updated);
      // Force re-render so the row swaps the delete icon for restore
      this.couponKeys[index] = Math.floor(Math.random() * 100000);
      this.$emit("campaign-changed");
    },

    /**
     * Restore a soft-deleted coupon to active status.
     */
    reactivateCoupon(index) {
      const existing = this.coupons[index];
      if (!existing) return;
      this.coupons.splice(index, 1, reactivateCouponHelper(existing));
      this.couponKeys[index] = Math.floor(Math.random() * 100000);
      this.$emit("campaign-changed");
    },

    /**
     * Rows iterate the filtered `visibleCoupons` set (single-source-state:
     * design-philosophy.md), so a per-row action must resolve the coupon's
     * real index in `this.coupons` by identity rather than trust a
     * filtered position. `visibleCoupons` is a `.filter()` over `coupons`,
     * which preserves object references, so `indexOf` by reference is
     * reliable here. Delegates to the existing index-based methods so the
     * soft-delete / hard-remove-unsaved contract stays in one place.
     */
    deleteCouponByRef(coupon) {
      if (this.isDeleteLockedByUnfrozenOrders(coupon)) return;
      if (this.usedOrderCount(coupon && coupon.name) > 0) {
        this.pendingUsedCouponDelete = coupon;
        return;
      }
      const index = this.coupons.indexOf(coupon);
      if (index === -1) return;
      this.deleteCoupon(index);
    },

    /**
     * Number of orders the delete warning names for ``name`` (not canceled,
     * not an abandoned checkout).
     */
    usedOrderCount(name) {
      const row = this.usedCouponRow(name);
      return row ? row.order_count : 0;
    },

    isDeleteLockedByUnfrozenOrders(coupon) {
      const row = this.usedCouponRow(coupon && coupon.name);
      return !!(row && row.has_unfrozen_orders);
    },

    usedCouponRow(name) {
      if (!name) return null;
      return this.usedCouponSummary.find((row) => row.code === name) || null;
    },

    /**
     * Confirms the used-coupon warning: soft-deletes through the same path
     * as an unused coupon. The orders keep the terms frozen on them.
     */
    confirmDeleteUsedCoupon() {
      const coupon = this.pendingUsedCouponDelete;
      this.pendingUsedCouponDelete = null;
      const index = this.coupons.indexOf(coupon);
      if (index === -1) return;
      this.deleteCoupon(index);
    },

    cancelDeleteUsedCoupon() {
      this.pendingUsedCouponDelete = null;
    },

    /**
     * Identity-based restore counterpart to deleteCouponByRef.
     */
    reactivateCouponByRef(coupon) {
      const index = this.coupons.indexOf(coupon);
      if (index === -1) return;
      this.reactivateCoupon(index);
    },

    /**
     * A coupon is bulk-deletable when it is currently active and its code
     * has not been redeemed on a placed order. A redeemed coupon is deleted
     * one at a time, behind the used-coupon warning.
     */
    isCouponDeletable(coupon) {
      if (!coupon) return false;
      if (!isCouponActive(coupon)) return false;
      return this.usedCouponList.indexOf(coupon.name) === -1;
    },

    /**
     * Opens the bulk soft-delete confirmation. The dialog reports the
     * deletable count; the actual mutation happens on confirm.
     */
    openDeleteVisibleDialog() {
      if (this.campaign.complete || this.deletableVisibleCount === 0) return;
      this.showDeleteVisibleDialog = true;
    },

    /**
     * Bulk soft-delete every deletable coupon in the current visible set
     * (respecting BOTH the filter and Show-deleted). Locked coupons are
     * skipped; already-deleted coupons are left untouched (idempotent).
     * Emits campaign-changed ONCE after the batch (autosave), then reports
     * "N deleted, M skipped (in use)".
     */
    confirmDeleteVisible() {
      // Snapshot the target set before mutating, so we iterate a stable
      // list rather than the reactive `visibleCoupons` as it shrinks.
      const targets = this.visibleCoupons.slice();
      let deleted = 0;
      let skipped = 0;

      targets.forEach((coupon) => {
        if (!isCouponActive(coupon)) return; // already deleted -- ignore
        if (this.usedCouponList.indexOf(coupon.name) > -1) {
          skipped++;
          return;
        }
        const index = this.coupons.indexOf(coupon);
        if (index === -1) return;
        const updated = softDeleteCoupon(coupon);
        if (updated === null) {
          // Unsaved entry -- hard-remove (no historical-order risk).
          this.coupons.splice(index, 1);
          this.couponKeys.splice(index, 1);
        } else {
          this.coupons.splice(index, 1, updated);
          this.couponKeys[index] = Math.floor(Math.random() * 100000);
        }
        deleted++;
      });

      if (deleted > 0) {
        this.$emit("campaign-changed");
      }
      this.deleteVisibleResult = `${deleted} deleted, ${skipped} skipped (in use)`;
      this.showDeleteVisibleDialog = false;
    },

    /**
     * Builds a CSV of the current visible set and triggers a client-side
     * download. Filename embeds the store slug (or campaign id) and the
     * current date. The Blob/anchor plumbing lives in triggerDownload so
     * the click handler stays unit-testable (tests stub triggerDownload).
     */
    downloadCsv() {
      const csv = buildCouponCsv(this.visibleCoupons);
      this.triggerDownload(this.csvFilename(), csv);
    },

    /**
     * Derives the export filename: coupons-<slug|id>-<YYYY-MM-DD>.csv,
     * falling back to coupons-<date>.csv when no slug/id is available.
     * `new Date()` is browser-runtime app code (not a workflow script),
     * so ISO-slicing the date here is fine.
     */
    csvFilename() {
      const date = new Date().toISOString().slice(0, 10);
      const slug =
        (this.campaign &&
          (this.campaign.slug ||
            (this.campaign._jv && this.campaign._jv.id))) ||
        "";
      return slug ? `coupons-${slug}-${date}.csv` : `coupons-${date}.csv`;
    },

    /**
     * Performs the actual browser download of `text` as `filename` via a
     * Blob + temporary anchor. Isolated so tests can stub it without
     * touching jsdom URL/Blob APIs.
     */
    triggerDownload(filename, text) {
      const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    /**
     * Default-true semantics: a coupon is active unless `active === false`.
     * Legacy entries (no `active` field) are treated as active.
     * Delegates to the pure helper so the predicate is unit-testable.
     */
    isCouponActive(coupon) {
      // Helper returns false for null/undefined; for template safety,
      // treat empty as active so an empty row stays in delete state.
      if (!coupon) return true;
      return isCouponActive(coupon);
    },

    /**
     * Checks whether a coupon code is unique across both the current
     * coupon list and the usedCouponList (codes already redeemed in
     * orders). Used by the add-row's validation.
     */
    isUniqueAddRowCode(code) {
      if (!code) return true;
      return !this.coupons.some((coupon) => coupon.name === code);
    },

    /**
     * Retained for existing callers/tests: same uniqueness semantics as
     * isUniqueAddRowCode, kept under its previous name.
     */
    isUniqueCoupon(code) {
      return this.isUniqueAddRowCode(code);
    },
  },
};
</script>

<style scoped>
/* ------------------------------------------------------------------
   Coupon block type + pill chrome (Jake, 2026-08-26, second pass).

   After retesting PR #2938 Jake reported the block "still looks weird":
   the "Add Coupon" label overflowed its pill, and "the font size [should
   be] more like other elements in this page -- the elements look huge
   relative to the stuff around them."

   TYPE SCALE. The block was riding two GLOBAL 16px scales while every
   neighbour in the Pricing section renders at 14px:

     .md-body-no-spacing            16px      style.css:473
     .extended-fab                  16px !important   style.css:552
     ---------------------------------------------------------------
     .pricing-master-row
       .pricing-master-label        14px      StoreFeatures.vue
     .editor-card__description      14px      components/EditorCard.vue

   14px is sampled from those two neighbours (they agree), not invented,
   and a test re-reads their declarations so a future move surfaces here
   rather than silently pinning a stale number.

   BUTTON CLIP. Two independent causes; this block fixes the first, and
   the add row's column split (see the template comment above the Add
   Coupon column) fixes the second. It was NOT width ALONE -- releasing
   the column without releasing this chrome would still have clipped.

   Cause 1, the fab chrome. `.extended-fab` in
   style.css hardcodes `height: 50px` and `font-size: 16px !important`,
   and `.extended-fab-icon.extended-fab-icon` forces a 30px glyph. When
   #2938 moved these pills to size="small", Vuetify set
   `--v-btn-height: 28px` / `--v-btn-size: 0.75rem`, but the !important
   type and the fixed height did not follow -- and Vuetify derives the
   icon's inline margins from `--v-btn-height`
   (`calc(var(--v-btn-height) / -9)`, VBtn.css), so a 30px icon received
   margins computed for a 28px box. A 16px `white-space: nowrap` label
   plus an oversized icon in a 28px pill is what pushed the text past the
   painted pill.

   The fix releases the fixed height (so the pill grows to fit its own
   content at any width) and brings the type and icon down onto the
   pill's own scale. No fixed width, no `overflow: hidden`, no ellipsis
   -- the label has to actually FIT, not be cropped politely.

   LOCAL, not global. `.extended-fab` is worn by pill buttons in
   StoreOrders and elsewhere, so this is a scoped
   `:deep()` override under `.coupon-scope` and the global rule is left
   exactly as it is. Scoped + a scope class means it reaches only the
   coupon drawer, not even the rest of StoreFeatures.
   ------------------------------------------------------------------ */
.coupon-scope {
  font-size: 14px;
}

.coupon-list-heading {
  font-size: 14px;
  font-weight: 600;
  /* .md-body-no-spacing painted this #424242. That bare hex is not
     allowed in a store-editor SFC (the G2 token sweep audits this file),
     and #424242 is not on the token scale anyway. --tf-fg-2 is the body
     token, one step darker, which is the correct semantic for a bold
     section heading. */
  color: var(--tf-fg-2, #333333);
}

.coupon-scope :deep(.extended-fab) {
  /* Release style.css's fixed 50px so the pill is sized by its content
     and by Vuetify's own --v-btn-height, instead of painting a 28px box
     around 50px of content. */
  height: auto;
  min-height: var(--v-btn-height, 28px);
  /* !important is required, not stylistic: the global declaration is
     itself !important, so a plain declaration loses at any specificity.
     13px sits one step under the 14px body scale, which is where a
     control label belongs relative to the text it acts on. */
  font-size: 13px !important;
  /* The global padding (0 20px 0 14px) was cut for a 50px pill. Pull it
     onto the token scale so the label has room without the pill
     ballooning. */
  padding: var(--tf-sp-1, 4px) var(--tf-sp-3, 12px);
}

.coupon-scope :deep(.extended-fab-icon.extended-fab-icon) {
  /* style.css doubles this selector for specificity, so the override
     must do the same. 18px is the Material default and sits correctly
     inside a small pill; 30px did not. */
  font-size: 18px;
}

.coupon-active-count {
  font-weight: 400;
  color: var(--tf-text-muted, #6b7280);
}

.coupon-filter-field {
  max-width: 260px;
  flex: 0 1 260px;
}

.coupon-bulk-result {
  font-size: 0.8125rem;
  color: var(--tf-text-muted, #6b7280);
}

/* Density pass (Jake, 2026-08-26): "the UI under Shopper Coupons uses
   really large elements". The add row was a 12px-padded box holding
   full-density Vuetify fields (56px tall each) and a size="large" pill
   button (52px), stacked under a header row of three more large pills.
   The fields drop to density="compact" (40px) and the buttons to
   size="small" (28px); this box tightens to match so the saving is not
   swallowed by its own padding. */
.coupon-add-row {
  background: var(--tf-surface-alt, #f5f5f5);
  border-radius: var(--tf-radius-xs, 4px);
  margin: 0 0 var(--tf-sp-2, 8px) 0;
  padding: var(--tf-sp-2, 8px) var(--tf-sp-1, 4px) var(--tf-sp-1, 4px)
    var(--tf-sp-1, 4px);
}

.coupon-column-headers {
  font-weight: 600;
  font-size: 0.8125rem;
  color: var(--tf-text-muted, #6b7280);
  padding: 4px 8px;
}

.coupon-row {
  padding: var(--tf-sp-1, 4px) var(--tf-sp-2, 8px);
  border-bottom: 1px solid var(--tf-surface-dark, #e8eff5);
}

.coupon-row:hover .coupon-col-actions {
  opacity: 1;
}

.coupon-col-code {
  flex: 0 0 30%;
}

.coupon-row-code {
  font-weight: 600;
}

.coupon-col-type {
  flex: 0 0 25%;
}

.coupon-col-amount {
  flex: 1 1 auto;
}

.coupon-col-actions {
  flex: 0 0 48px;
  justify-content: flex-end;
}

.coupon-footer {
  border-top: 1px solid var(--tf-surface-dark, #e8eff5);
  padding-top: var(--tf-sp-2, 8px);
}

.coupon-lock-note {
  color: var(--tf-text-muted, #6b7280);
  margin-top: 4px;
}
</style>
